import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { Survey, SurveyResponse } from '../../types/survey';
import { useLanguage } from '../../contexts/LanguageContext';

const SurveyCompare: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [results, setResults] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRespondents, setSelectedRespondents] = useState<string[]>([]);

  useEffect(() => {
    if (surveyId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSurveyResults(surveyId!);
      setSurvey(data.survey);
      setResults(data.results);
      // Select all respondents by default
      setSelectedRespondents(data.results.map(r => r.email));
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleRespondent = (email: string) => {
    setSelectedRespondents(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  if (loading) return <div style={{ padding: '30px' }}>{t('common', 'loading')}</div>;
  if (!survey) return <div style={{ padding: '30px' }}>{t('surveyResults', 'dataNotFound')}</div>;

  const filteredResults = results.filter(r => selectedRespondents.includes(r.email));

  // Get all questions from the first result
  const questions = results.length > 0 && results[0].responses ? results[0].responses : [];

  return (
    <div style={{ padding: '30px', maxWidth: '100%', margin: '0 auto' }}>
      <button
        onClick={() => navigate(`/admin/surveys/${surveyId}/results`)}
        style={{ marginBottom: '16px', padding: '6px 12px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#4a5568' }}
      >
        {t('surveyCompare', 'backToResults')}
      </button>

      <h2 style={{ marginBottom: '16px' }}>{survey.title} - {t('surveyCompare', 'comparison')}</h2>

      {/* Respondent filter */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {results.map(r => (
          <label key={r.email} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            backgroundColor: selectedRespondents.includes(r.email) ? '#ebf8ff' : '#f7fafc',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            border: `1px solid ${selectedRespondents.includes(r.email) ? '#90cdf4' : '#e2e8f0'}`
          }}>
            <input
              type="checkbox"
              checked={selectedRespondents.includes(r.email)}
              onChange={() => toggleRespondent(r.email)}
            />
            {r.respondentName}
          </label>
        ))}
      </div>

      {/* Comparison table */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f7fafc' }}>
              <th style={{ padding: '10px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', minWidth: '40px', position: 'sticky', left: 0, backgroundColor: '#f7fafc' }}>No</th>
              <th style={{ padding: '10px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', minWidth: '100px', position: 'sticky', left: '40px', backgroundColor: '#f7fafc' }}>{t('surveyCompare', 'category')}</th>
              <th style={{ padding: '10px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', minWidth: '250px', position: 'sticky', left: '140px', backgroundColor: '#f7fafc' }}>{t('surveyCompare', 'question')}</th>
              {filteredResults.map(r => (
                <th key={r.email} style={{ padding: '10px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', minWidth: '200px' }}>
                  {r.respondentName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {questions.map((q, qi) => (
              <tr key={qi} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px', fontSize: '0.8rem', color: '#718096', position: 'sticky', left: 0, backgroundColor: 'white' }}>{q.no}</td>
                <td style={{ padding: '10px', fontSize: '0.8rem', color: '#718096', position: 'sticky', left: '40px', backgroundColor: 'white' }}>{q.category}</td>
                <td style={{ padding: '10px', position: 'sticky', left: '140px', backgroundColor: 'white' }}>{q.question}</td>
                {filteredResults.map(r => {
                  const resp = r.responses?.find(rr => rr.no === q.no || rr.question === q.question);
                  return (
                    <td key={r.email} style={{ padding: '10px', color: resp?.answer ? '#2d3748' : '#cbd5e0' }}>
                      {resp?.answer || '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SurveyCompare;
